import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Settings, Users, Clock, Shield, MapPin, Save } from 'lucide-react';
import { toast } from 'sonner';

interface RestrictionsConfigProps {
  gameId: string;
  gameType: 'regular' | 'venue';
  isVisible: boolean;
}

interface GameAccessRestrictions {
  id?: string;
  allowed_roles: string[];
  allowed_age_groups: string[];
  require_team_affiliation: boolean;
  approval_required_from: string[];
  registration_deadline?: string;
  max_advance_registration_hours?: number;
  max_participants_per_role: Record<string, number> | any;
  max_total_participants?: number;
  restriction_message?: string;
}

const DEFAULT_RESTRICTIONS: GameAccessRestrictions = {
  allowed_roles: ['player', 'coach', 'scorekeeper', 'team-owner'],
  allowed_age_groups: ['under-18', '18-plus'],
  require_team_affiliation: false,
  approval_required_from: ['coach'],
  max_participants_per_role: {},
  restriction_message: ''
};

const ROLE_OPTIONS = [
  { value: 'player', label: 'Players', description: 'Active game participants' },
  { value: 'coach', label: 'Coaches', description: 'Team coaches and assistants' },
  { value: 'scorekeeper', label: 'Scorekeepers', description: 'Official game scorekeepers' },
  { value: 'team-owner', label: 'Team Owners', description: 'Team managers and owners' }
];

const AGE_GROUP_OPTIONS = [
  { value: 'under-18', label: 'Under 18', description: 'Players under 18 years old' },
  { value: '18-plus', label: '18+', description: 'Players 18 years and older' }
];

const APPROVAL_OPTIONS = [
  { value: 'coach', label: 'Coach', description: 'Team coaches can approve' },
  { value: 'team-owner', label: 'Team Owner', description: 'Team owners can approve' },
  { value: 'venue-manager', label: 'Venue Manager', description: 'Venue managers can approve' }
];

export const RestrictionsConfig: React.FC<RestrictionsConfigProps> = ({
  gameId,
  gameType,
  isVisible
}) => {
  const [restrictions, setRestrictions] = useState<GameAccessRestrictions>(DEFAULT_RESTRICTIONS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isVisible) {
      loadRestrictions();
    }
  }, [isVisible, gameId]);

  const loadRestrictions = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('game_access_restrictions')
        .select('*')
        .eq('game_id', gameId)
        .eq('game_type', gameType)
        .single();

      if (error && error.code !== 'PGRST116') { // Not found error
        throw error;
      }

      if (data) {
        // Ensure max_participants_per_role is properly typed
        const processedData = {
          ...data,
          max_participants_per_role: typeof data.max_participants_per_role === 'object' && data.max_participants_per_role !== null 
            ? data.max_participants_per_role as Record<string, number>
            : {}
        };
        setRestrictions(processedData);
      } else {
        setRestrictions(DEFAULT_RESTRICTIONS);
      }
    } catch (error) {
      console.error('Error loading restrictions:', error);
      toast.error('Failed to load restriction settings');
    } finally {
      setLoading(false);
    }
  };

  const saveRestrictions = async () => {
    try {
      setSaving(true);
      
      const restrictionData = {
        game_id: gameId,
        game_type: gameType,
        ...restrictions
      };

      if (restrictions.id) {
        // Update existing
        const { error } = await supabase
          .from('game_access_restrictions')
          .update(restrictionData)
          .eq('id', restrictions.id);

        if (error) throw error;
      } else {
        // Create new
        const { data, error } = await supabase
          .from('game_access_restrictions')
          .insert([restrictionData])
          .select()
          .single();

        if (error) throw error;
        setRestrictions({ ...restrictions, id: data.id });
      }

      toast.success('Restriction settings saved successfully');
    } catch (error) {
      console.error('Error saving restrictions:', error);
      toast.error('Failed to save restriction settings');
    } finally {
      setSaving(false);
    }
  };

  const updateRestrictions = (field: keyof GameAccessRestrictions, value: any) => {
    setRestrictions(prev => ({ ...prev, [field]: value }));
  };

  const toggleRole = (role: string) => {
    const currentRoles = restrictions.allowed_roles || [];
    const newRoles = currentRoles.includes(role)
      ? currentRoles.filter(r => r !== role)
      : [...currentRoles, role];
    updateRestrictions('allowed_roles', newRoles);
  };

  const toggleAgeGroup = (ageGroup: string) => {
    const currentGroups = restrictions.allowed_age_groups || [];
    const newGroups = currentGroups.includes(ageGroup)
      ? currentGroups.filter(g => g !== ageGroup)
      : [...currentGroups, ageGroup];
    updateRestrictions('allowed_age_groups', newGroups);
  };

  const toggleApprovalRole = (role: string) => {
    const currentRoles = restrictions.approval_required_from || [];
    const newRoles = currentRoles.includes(role)
      ? currentRoles.filter(r => r !== role)
      : [...currentRoles, role];
    updateRestrictions('approval_required_from', newRoles);
  };

  const updateParticipantLimit = (role: string, limit: number) => {
    const newLimits = { ...restrictions.max_participants_per_role };
    if (limit <= 0) {
      delete newLimits[role];
    } else {
      newLimits[role] = limit;
    }
    updateRestrictions('max_participants_per_role', newLimits);
  };

  if (!isVisible) return null;

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Restriction Configuration
        </CardTitle>
        <CardDescription>
          Configure detailed access restrictions for this game
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading ? (
          <div className="text-center py-4 text-muted-foreground">
            Loading restriction settings...
          </div>
        ) : (
          <>
            {/* Role-based Restrictions */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                <Label className="font-medium">Allowed Roles</Label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {ROLE_OPTIONS.map((role) => (
                  <div key={role.value} className="flex items-start space-x-2 p-3 border rounded-lg">
                    <Checkbox
                      id={`role-${role.value}`}
                      checked={restrictions.allowed_roles?.includes(role.value) || false}
                      onCheckedChange={() => toggleRole(role.value)}
                    />
                    <div className="flex-1">
                      <Label htmlFor={`role-${role.value}`} className="text-sm font-medium">
                        {role.label}
                      </Label>
                      <p className="text-xs text-muted-foreground">{role.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* Age Group Restrictions */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                <Label className="font-medium">Allowed Age Groups</Label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {AGE_GROUP_OPTIONS.map((ageGroup) => (
                  <div key={ageGroup.value} className="flex items-start space-x-2 p-3 border rounded-lg">
                    <Checkbox
                      id={`age-${ageGroup.value}`}
                      checked={restrictions.allowed_age_groups?.includes(ageGroup.value) || false}
                      onCheckedChange={() => toggleAgeGroup(ageGroup.value)}
                    />
                    <div className="flex-1">
                      <Label htmlFor={`age-${ageGroup.value}`} className="text-sm font-medium">
                        {ageGroup.label}
                      </Label>
                      <p className="text-xs text-muted-foreground">{ageGroup.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* Approval Workflow */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                <Label className="font-medium">Who Can Approve Join Requests</Label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {APPROVAL_OPTIONS.map((approver) => (
                  <div key={approver.value} className="flex items-start space-x-2 p-3 border rounded-lg">
                    <Checkbox
                      id={`approver-${approver.value}`}
                      checked={restrictions.approval_required_from?.includes(approver.value) || false}
                      onCheckedChange={() => toggleApprovalRole(approver.value)}
                    />
                    <div className="flex-1">
                      <Label htmlFor={`approver-${approver.value}`} className="text-sm font-medium">
                        {approver.label}
                      </Label>
                      <p className="text-xs text-muted-foreground">{approver.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* Time-based Restrictions */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <Label className="font-medium">Time Restrictions</Label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="registration-deadline" className="text-sm">
                    Registration Deadline
                  </Label>
                  <Input
                    id="registration-deadline"
                    type="datetime-local"
                    value={restrictions.registration_deadline?.slice(0, 16) || ''}
                    onChange={(e) => updateRestrictions('registration_deadline', 
                      e.target.value ? new Date(e.target.value).toISOString() : null
                    )}
                  />
                  <p className="text-xs text-muted-foreground">
                    No new participants after this time
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max-advance-hours" className="text-sm">
                    Max Advance Registration (hours)
                  </Label>
                  <Input
                    id="max-advance-hours"
                    type="number"
                    min="0"
                    value={restrictions.max_advance_registration_hours || ''}
                    onChange={(e) => updateRestrictions('max_advance_registration_hours', 
                      e.target.value ? parseInt(e.target.value) : null
                    )}
                  />
                  <p className="text-xs text-muted-foreground">
                    How far in advance players can join
                  </p>
                </div>
              </div>
            </div>

            <Separator />

            {/* Participant Limits */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                <Label className="font-medium">Participant Limits</Label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="max-total" className="text-sm">
                    Maximum Total Participants
                  </Label>
                  <Input
                    id="max-total"
                    type="number"
                    min="0"
                    value={restrictions.max_total_participants || ''}
                    onChange={(e) => updateRestrictions('max_total_participants', 
                      e.target.value ? parseInt(e.target.value) : null
                    )}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Limits per Role</Label>
                  <div className="space-y-2">
                    {ROLE_OPTIONS.map((role) => (
                      <div key={role.value} className="flex items-center gap-2">
                        <Label className="text-xs w-20">{role.label}:</Label>
                        <Input
                          type="number"
                          min="0"
                          className="flex-1"
                          value={restrictions.max_participants_per_role?.[role.value] || ''}
                          onChange={(e) => updateParticipantLimit(role.value, parseInt(e.target.value) || 0)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Custom Message */}
            <div className="space-y-3">
              <Label className="font-medium">Custom Restriction Message</Label>
              <Textarea
                placeholder="Enter a custom message to display to users when restrictions apply..."
                value={restrictions.restriction_message || ''}
                onChange={(e) => updateRestrictions('restriction_message', e.target.value)}
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                This message will be shown to users who don't meet the restrictions
              </p>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4">
              <Button onClick={saveRestrictions} disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Restrictions'}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};